import React, { useRef } from 'react';
import { MedicalRecord, PrescriptionItem, useApp, formatHumaneRovitaLicenseId } from './AppContext';
import { BrandLogo } from './Components';

interface DigitalPrescriptionModalProps {
  record?: MedicalRecord | null;
  hideDiagnosis?: boolean;
  // Allow passing raw fields directly if previewing before saving
  customData?: {
    rxId?: string;
    date?: string;
    issuedAt?: string;
    doctorName?: string;
    doctorSpecialty?: string;
    doctorLicenseNumber?: string;
    patientName?: string;
    patientMrn?: string;
    patientAge?: string;
    patientGender?: string;
    patientAllergies?: string;
    diagnosis?: string;
    items?: PrescriptionItem[];
    rawPrescriptionText?: string;
    treatmentPlan?: string;
    dietaryAdvice?: string;
    generalPrecautions?: string;
    followUpInstructions?: string;
    notes?: string;
  };
  onWriteNew?: () => void;
  onClose: () => void;
}

export const DigitalPrescriptionModal: React.FC<DigitalPrescriptionModalProps> = ({
  record,
  hideDiagnosis = false,
  customData,
  onWriteNew,
  onClose
}) => {
  const { showToast } = useApp();
  const printRef = useRef<HTMLDivElement>(null);

  // Extract all data from record or custom preview data
  const rxId = record?.rxId || customData?.rxId || `ROVITA-RX-${new Date().getFullYear()}-${record?.id ? record.id.slice(0, 6).toUpperCase() : 'PENDING'}`;
  const issueDate = record?.issuedAt ? new Date(record.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : (customData?.issuedAt ? new Date(customData.issuedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : (record?.date || customData?.date || new Date().toLocaleDateString()));
  const doctorName = record?.doctorName || customData?.doctorName || 'ROVITA Medical Specialist';
  const doctorSpecialty = record?.doctorSpecialty || customData?.doctorSpecialty || 'General Telemedicine Practitioner';
  const doctorLicense = record?.doctorLicenseNumber || customData?.doctorLicenseNumber || (record ? formatHumaneRovitaLicenseId(record) : 'ROV-MD-10101');
  const patientName = record?.patientName || customData?.patientName || 'Registered Patient';
  const patientMrn = record?.patientMrn || customData?.patientMrn || (record?.patientId ? `MRN-${record.patientId.slice(0, 8).toUpperCase()}` : 'ROVITA-PT-CURRENT');
  const patientAge = record?.patientAge || customData?.patientAge || 'Adult';
  const patientGender = record?.patientGender || customData?.patientGender || 'Unspecified';
  const patientAllergies = record?.patientAllergies || customData?.patientAllergies || 'No known drug allergies reported (NKDA)';
  const diagnosis = record?.diagnosis || customData?.diagnosis || 'Clinical Consultation';
  const dietaryAdvice = record?.dietaryAdvice || customData?.dietaryAdvice;
  const generalPrecautions = record?.generalPrecautions || customData?.generalPrecautions;
  const followUpInstructions = record?.followUpInstructions || customData?.followUpInstructions;
  const treatmentPlan = record?.treatmentPlan || customData?.treatmentPlan;

  // Extract items
  const items: PrescriptionItem[] = record?.prescriptionItems && record.prescriptionItems.length > 0 
    ? record.prescriptionItems 
    : (customData?.items && customData.items.length > 0 
      ? customData.items 
      : []);

  const rawPrescription = record?.prescription || customData?.rawPrescriptionText || '';

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = () => {
    let text = `ROVITA TELEHEALTH DIGITAL PRESCRIPTION (${rxId})\n`;
    text += `Date: ${issueDate}\n`;
    text += `Doctor: ${doctorName} (${doctorSpecialty})\n`;
    text += `Patient: ${patientName} (${patientMrn})\n`;
    text += `Diagnosis: ${diagnosis}\n\n`;
    text += `--- MEDICATIONS (Rx) ---\n`;
    if (items.length > 0) {
      items.forEach((it, idx) => {
        text += `${idx + 1}. ${it.name} ${it.dosage || ''} [${it.form || 'Medication'}] - ${it.frequency} x ${it.duration}\n   Directions: ${it.instructions || 'As directed'}\n`;
      });
    } else if (rawPrescription) {
      text += `${rawPrescription}\n`;
    }
    if (dietaryAdvice) text += `\nDietary Advice: ${dietaryAdvice}\n`;
    if (generalPrecautions) text += `Precautions: ${generalPrecautions}\n`;
    if (followUpInstructions) text += `Follow-up: ${followUpInstructions}\n`;

    navigator.clipboard.writeText(text);
    showToast('Prescription Copied', 'Digital Prescription summary copied to clipboard.', 'success');
  };

  return (
    <div className="fixed inset-0 z-[400] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto font-['Plus_Jakarta_Sans'] animate-in fade-in duration-200">
      {/* Container */}
      <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden my-auto flex flex-col border border-slate-200">
        
        {/* Top Control Bar (Hidden on Print) */}
        <div className="p-3 sm:p-5 bg-slate-900 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 print:hidden">
          <div className="flex items-center justify-between sm:justify-start gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center text-indigo-400 shrink-0">
                <i className="fa-solid fa-prescription-bottle-medical text-base sm:text-lg"></i>
              </div>
              <div>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-indigo-400">Official Electronic Prescription</span>
                  <span className="text-[9px] sm:text-[10px] bg-emerald-500/20 text-emerald-300 font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">Verified Rx</span>
                </div>
                <h2 className="text-xs sm:text-base font-bold text-slate-100 font-mono tracking-tight">{rxId}</h2>
              </div>
            </div>

            {/* Mobile Close Button in Header Top */}
            <button
              onClick={onClose}
              className="sm:hidden w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-all shrink-0"
              aria-label="Close prescription modal"
            >
              <i className="fa-solid fa-xmark text-sm"></i>
            </button>
          </div>

          <div className="flex items-center justify-end gap-2 sm:gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800/80">
            <button
              onClick={handleCopy}
              className="flex-1 sm:flex-none px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 active:scale-95 border border-slate-700"
              title="Copy text summary"
            >
              <i className="fa-solid fa-copy text-xs"></i>
              <span>Copy Text</span>
            </button>
            {onWriteNew && (
              <button
                onClick={() => {
                  onClose();
                  onWriteNew();
                }}
                className="flex-1 sm:flex-none px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                title="Write a new digital prescription"
              >
                <i className="fa-solid fa-plus text-xs"></i>
                <span>New Prescription</span>
              </button>
            )}
            <button
              onClick={handlePrint}
              className="flex-1 sm:flex-none px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 active:scale-95"
              title="Print official prescription or save as PDF"
            >
              <i className="fa-solid fa-print text-xs"></i>
              <span>Print / Save PDF</span>
            </button>
            <button
              onClick={onClose}
              className="hidden sm:flex w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white items-center justify-center transition-all shrink-0"
              aria-label="Close prescription modal"
            >
              <i className="fa-solid fa-xmark text-sm"></i>
            </button>
          </div>
        </div>

        {/* Prescription Paper Document Content */}
        <div 
          ref={printRef}
          className="p-4 sm:p-8 md:p-12 bg-white text-slate-900 space-y-6 max-h-[85vh] sm:max-h-[80vh] overflow-y-auto print:max-h-none print:overflow-visible print:p-8"
        >
          {/* Document Header with Rovita Branding */}
          <div className="border-b-2 border-indigo-600 pb-5 sm:pb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center p-2 shadow-2xs shrink-0">
                  <img 
                    src="/logo.png" 
                    alt="ROVITA Logo" 
                    className="w-full h-full object-contain"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">ROVITA</span>
                    <span className="text-[9px] sm:text-[10px] font-extrabold uppercase px-2 py-0.5 bg-indigo-600 text-white rounded-md tracking-wider">Telemedicine Rx</span>
                  </div>
                  <p className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Virtual Medical Consultation Network</p>
                  <p className="text-[10px] sm:text-[11px] text-slate-400">Accredited Digital Healthcare & Telehealth Services</p>
                </div>
              </div>

              <div className="text-left sm:text-right bg-slate-50 sm:bg-transparent p-3 sm:p-0 rounded-2xl sm:rounded-none w-full sm:w-auto border sm:border-0 border-slate-100">
                <div className="inline-block px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-lg text-xs font-mono font-bold tracking-wider mb-1">
                  {rxId}
                </div>
                <p className="text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-700">Issued On:</span> {issueDate}
                </p>
                <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 sm:justify-end">
                  <i className="fa-solid fa-circle-check text-[10px]"></i> Authorized Digital Document
                </p>
              </div>
            </div>
          </div>

          {/* Clinician & Patient Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Doctor Credentials */}
            <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200/80 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-full -mr-8 -mt-8 pointer-events-none"></div>
              <div className="flex items-center gap-2 mb-2">
                <i className="fa-solid fa-user-doctor text-indigo-600 text-sm"></i>
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-600">Prescribing Clinician</h3>
              </div>
              <h4 className="text-base font-black text-slate-900">{doctorName}</h4>
              <p className="text-xs font-bold text-indigo-600 mt-0.5">{doctorSpecialty}</p>
              <div className="mt-3 pt-3 border-t border-slate-200/60 text-[11px] space-y-1 text-slate-600">
                <p><span className="font-semibold text-slate-700">License ID:</span> <span className="font-mono text-slate-800 font-bold">{doctorLicense}</span></p>
                <p><span className="font-semibold text-slate-700">Provider Network:</span> ROVITA Telehealth International</p>
              </div>
            </div>

            {/* Patient Credentials */}
            <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-200/80 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-8 -mt-8 pointer-events-none"></div>
              <div className="flex items-center gap-2 mb-2">
                <i className="fa-solid fa-hospital-user text-emerald-600 text-sm"></i>
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-600">Patient Information</h3>
              </div>
              <h4 className="text-base font-black text-slate-900">{patientName}</h4>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-[11px] font-mono font-bold bg-white px-2 py-0.5 rounded-md border border-slate-200 text-slate-700">{patientMrn}</span>
                <span className="text-[11px] text-slate-500 font-medium">Age: <strong className="text-slate-800">{patientAge}</strong></span>
                <span className="text-[11px] text-slate-500 font-medium">Gender: <strong className="text-slate-800 capitalize">{patientGender}</strong></span>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200/60 text-[11px] text-slate-600">
                <p className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-slate-700">Allergies:</span>
                  <span className={`font-semibold px-2 py-0.5 rounded text-[10px] ${patientAllergies.toLowerCase().includes('no') || patientAllergies.toLowerCase().includes('nkda') ? 'bg-slate-200/60 text-slate-700' : 'bg-rose-100 text-rose-800'}`}>
                    {patientAllergies}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Clinical Indication / Diagnosis Banner */}
          {!hideDiagnosis && (
            <div className="p-4 bg-indigo-50/70 rounded-2xl border border-indigo-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-stethoscope text-xs"></i>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Primary Diagnosis / Clinical Indication</p>
                  <p className="text-sm font-black text-slate-900">{diagnosis}</p>
                </div>
              </div>
              {treatmentPlan && (
                <div className="text-xs text-slate-600 max-w-sm">
                  <span className="font-bold text-slate-700">Treatment Plan:</span> {treatmentPlan}
                </div>
              )}
            </div>
          )}

          {/* Prescribed Medications (Rx Table for Desktop/Print + Native Cards for Mobile) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-serif font-black text-indigo-600">℞</span>
                <h3 className="text-sm font-black uppercase tracking-wider text-slate-900">Prescribed Medications</h3>
              </div>
              <span className="text-xs text-slate-400 font-medium">Take strictly as indicated</span>
            </div>

            {items.length > 0 ? (
              <>
                {/* 1. MOBILE RESPONSIVE CARDS VIEW (Visible only on mobile screen < sm, hidden in print) */}
                <div className="sm:hidden space-y-3 print:hidden">
                  {items.map((it, idx) => (
                    <div key={it.id || idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-black text-[11px] flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <div>
                            <h4 className="font-black text-slate-900 text-sm leading-tight">{it.name}</h4>
                            {it.form && (
                              <span className="inline-block mt-0.5 text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                                {it.form}
                              </span>
                            )}
                          </div>
                        </div>
                        {it.dispenseQuantity && (
                          <span className="text-[10px] font-bold text-slate-600 bg-slate-200/70 px-2 py-0.5 rounded-md shrink-0">
                            Qty: {it.dispenseQuantity}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200/70 text-xs">
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Dosage</p>
                          <p className="font-black text-slate-800 mt-0.5">{it.dosage || 'Standard'}</p>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Duration</p>
                          <p className="font-black text-slate-800 mt-0.5">{it.duration || 'As directed'}</p>
                        </div>
                      </div>

                      <div className="bg-indigo-50/60 p-2.5 rounded-xl border border-indigo-100 text-xs">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1">
                          <i className="fa-solid fa-clock text-[10px]"></i> Frequency & Timing
                        </p>
                        <p className="font-black text-indigo-950 mt-0.5">{it.frequency}</p>
                      </div>

                      {it.instructions && (
                        <div className="text-[11px] text-slate-600 bg-white p-2.5 rounded-xl border border-slate-100">
                          <strong className="text-slate-700">Instructions:</strong> {it.instructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 2. DESKTOP / TABLET / PRINT TABLE VIEW (Visible on sm: and above, always used in print) */}
                <div className="hidden sm:block print:block border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-700 font-black uppercase text-[10px] tracking-wider border-b border-slate-200">
                        <tr>
                          <th className="py-3 px-4 w-12 text-center">#</th>
                          <th className="py-3 px-4">Medication & Form</th>
                          <th className="py-3 px-4">Dosage / Strength</th>
                          <th className="py-3 px-4">Frequency & Timing</th>
                          <th className="py-3 px-4">Duration</th>
                          <th className="py-3 px-4">Specific Instructions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {items.map((it, idx) => (
                          <tr key={it.id || idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-3.5 px-4 font-bold text-slate-400 text-center">{idx + 1}</td>
                            <td className="py-3.5 px-4">
                              <p className="font-black text-slate-900 text-sm">{it.name}</p>
                              {it.form && (
                                <span className="inline-block mt-0.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                                  {it.form}
                                </span>
                              )}
                            </td>
                            <td className="py-3.5 px-4 font-bold text-slate-800">
                              {it.dosage || 'Standard'}
                              {it.dispenseQuantity && (
                                <p className="text-[10px] text-slate-400 font-medium mt-0.5">Qty: {it.dispenseQuantity}</p>
                              )}
                            </td>
                            <td className="py-3.5 px-4">
                              <span className="font-bold text-slate-800 bg-slate-100 px-2 py-1 rounded-md text-[11px]">
                                {it.frequency}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 font-bold text-slate-700">{it.duration}</td>
                            <td className="py-3.5 px-4 text-slate-600 text-[11px] max-w-xs">
                              {it.instructions || 'As advised by doctor'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : rawPrescription ? (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl whitespace-pre-line text-xs font-mono text-slate-800 leading-relaxed break-words">
                {rawPrescription}
              </div>
            ) : (
              <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center text-xs text-slate-500">
                No specific medication items listed for this consultation.
              </div>
            )}
          </div>

          {/* Advice, Diet & Follow-up Section */}
          {(dietaryAdvice || generalPrecautions || followUpInstructions) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              {dietaryAdvice && (
                <div className="p-4 bg-amber-50/60 rounded-2xl border border-amber-200/60 text-xs">
                  <p className="font-black text-amber-900 uppercase text-[10px] tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i className="fa-solid fa-utensils text-amber-600"></i>
                    Dietary & Lifestyle Advice
                  </p>
                  <p className="text-slate-700 leading-relaxed">{dietaryAdvice}</p>
                </div>
              )}

              {generalPrecautions && (
                <div className="p-4 bg-rose-50/60 rounded-2xl border border-rose-200/60 text-xs">
                  <p className="font-black text-rose-900 uppercase text-[10px] tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i className="fa-solid fa-triangle-exclamation text-rose-600"></i>
                    Precautions & Warnings
                  </p>
                  <p className="text-slate-700 leading-relaxed">{generalPrecautions}</p>
                </div>
              )}

              {followUpInstructions && (
                <div className="p-4 bg-indigo-50/60 rounded-2xl border border-indigo-200/60 text-xs">
                  <p className="font-black text-indigo-900 uppercase text-[10px] tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i className="fa-solid fa-calendar-check text-indigo-600"></i>
                    Follow-up Instructions
                  </p>
                  <p className="text-slate-700 leading-relaxed">{followUpInstructions}</p>
                </div>
              )}
            </div>
          )}

          {/* Digital Signature & Verification Security Stamp */}
          <div className="pt-6 border-t-2 border-dashed border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6">
            <div className="space-y-2 max-w-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs">
                  <i className="fa-solid fa-shield-halved"></i>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Digital Telehealth Verification</span>
              </div>
              <p className="text-[10px] text-slate-500 leading-normal">
                This electronic prescription was created and issued via the ROVITA Telemedicine Platform for clinical consultation and pharmacy dispensing. ROVITA operates as a software facilitation service and is not subject to legal jurisdiction, expert testimony, or court-related proceedings.
              </p>
              <p className="text-[9px] font-mono text-slate-400">
                Security Hash: SHA256:{rxId.replace(/[^a-zA-Z0-9]/g, '')}F928A4B01E8
              </p>
            </div>

            <div className="text-left sm:text-right border-t sm:border-t-0 pt-4 sm:pt-0 w-full sm:w-auto">
              <div className="inline-block border border-indigo-200 bg-indigo-50/50 p-3 rounded-2xl text-left">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fa-solid fa-file-signature text-indigo-600"></i>
                  <span className="text-[10px] font-black uppercase tracking-wider text-indigo-900">Physician Electronic Signature</span>
                </div>
                <div className="font-serif italic text-base font-bold text-slate-800 py-1">
                  {doctorName}
                </div>
                <p className="text-[9px] font-mono text-slate-500">
                  Signed at: {issueDate}
                </p>
                <p className="text-[9px] text-emerald-600 font-bold mt-0.5">
                  ✓ Certified Practitioner Signature
                </p>
              </div>
            </div>
          </div>

          {/* Document Footer Note */}
          <div className="text-center pt-4 border-t border-slate-100 text-[10px] text-slate-400 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>ROVITA Virtual Telehealth Network • rovita-health.com</span>
            <span>Emergency Disclaimer: In case of acute severe distress, call local emergency services immediately.</span>
          </div>

        </div>

      </div>
    </div>
  );
};
